import { from, Observable, throwError as observableThrowError } from 'rxjs';

import { catchError, switchMap, tap } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NotificationsService } from 'angular2-notifications';
import { AuthenticationService } from './../../../sb-tools/typescript/authentication/authentication.service';
import { environment } from '@environment';
import { ErrorEnum } from '../enums/error.enum';
import { TranslateService } from '@ngx-translate/core';
import { InitializationService } from './initialization.service';

@Injectable()
export class Interceptor implements HttpInterceptor {
    constructor(
        private authenticationService: AuthenticationService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private initializationService: InitializationService,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let headers = req.headers.set('Version', environment.apiVersion);

        if (req.url.toLowerCase().indexOf('oauth/token') === -1) {
            const community = this.initializationService.communityName || this.authenticationService.community;
            return from(this.authenticationService.getAuthorizationHeader(community)).pipe(
                switchMap((data) => {
                    headers = headers.set('Authorization', data.toString()).set('Community', community);

                    //   .append('Content-Type', application/json');
                    const clonedRequest = req.clone({
                        headers,
                    });

                    // Pass the cloned request instead of the original request to the next handle
                    return next.handle(clonedRequest).pipe(
                        tap((event) => {
                            return this.setServerTime(event);
                        }),
                        catchError((response) => {
                            return this.handleError(response, req);
                        }),
                    );
                }),
            );
        } else {
            // Pass the cloned request instead of the original request to the next handle
            const clonedRequest = req.clone({
                headers,
            });
            return next.handle(clonedRequest).pipe(
                tap((event) => {
                    return this.setServerTime(event);
                }),
                catchError((response) => {
                    return this.handleError(response, clonedRequest);
                }),
            );
        }
    }

    private handleError(response, req) {
        if (this.authenticationService.isLoggingOut) {
            return observableThrowError(response);
        }

        if (req.url.indexOf('oauth/token') !== -1) {
            this.authenticationService.refreshPromise = null;
        }

        if (!response.error) {
            response.error = {};
        }

        let message;
        if (response.status === 400 && req.url.indexOf('oauth/token') !== -1) {
            if (response.error.errorId === ErrorEnum.sessionExpired) {
                this.authenticationService.logOff({ isNeedRedirect: true });
                return observableThrowError(response);
            }

            if (response.error.errorId === ErrorEnum.communityWithNameNotFound) {
                this.authenticationService.redirectToCommunities();
                return observableThrowError(response);
            }

            message = response.error['message'] || response.error['error_description'];
            if (message) {
                this.notificationsService.error(this.translateService.instant('Error!'), message);
            }

            this.authenticationService.logOff({ isNeedRedirect: true });
        } else if (response.status === 401) {
            this.authenticationService.needToRefreshToken = true;
        } else if (response.error && response.error.errorId === ErrorEnum.communityWithNameNotFound) {
            window.location.pathname = '/404';
        } else {
            if (response.error && (typeof response.error === 'string' || response.error.message)) {
                message = response.error && response.error.message ? response.error.message : response.error;
                if (message.toLowerCase().indexOf('timeout') >= 0) {
                    message = this.translateService.instant(
                        'Oops... your action was not completed due to a slow Internet connection. Please wait for a few seconds and try again.',
                    );
                }
            } else {
                message = this.translateService.instant(
                    'Oops... Something went wrong, please try again or reload this page',
                );
            }

            this.notificationsService.error(this.translateService.instant('Error!'), message);
        }

        return observableThrowError(response);
    }

    private setServerTime(event) {
        if (event.type !== 0 && event['url'] && event['url'].indexOf(environment.apiMasterUrl) === 0) {
            const serverTime = event['headers'].get('date');
            this.authenticationService.setTimeDifference(serverTime);
        }
    }
}
